import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  alpha,
  useTheme,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useGetProductsQuery } from '../../../store/api/productsApi';
import { useGetSalesOrdersQuery, useGetSalesReturnsQuery } from '../../../store/api/salesApi';
import { useGetPurchaseOrdersQuery, useGetPurchaseReturnsQuery } from '../../../store/api/purchaseApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { REPORT_CATEGORIES } from '../config/reportCatalog';

const CATEGORY_ICONS = {
  inventory: Inventory2OutlinedIcon,
  purchase: ShoppingCartOutlinedIcon,
  sales: PointOfSaleOutlinedIcon,
  financial: AccountBalanceOutlinedIcon,
  management: DashboardOutlinedIcon,
  compliance: GavelOutlinedIcon,
  'van-sales': DirectionsCarOutlinedIcon,
};

const STAT_CARDS = [
  { key: 'products', label: 'Total Products', color: 'primary', icon: BarChartRoundedIcon },
  { key: 'sales', label: 'Net Sales', color: 'success', icon: PointOfSaleOutlinedIcon },
  { key: 'purchases', label: 'Net Purchases', color: 'warning', icon: ShoppingCartOutlinedIcon },
  { key: 'profit', label: 'Gross Profit', color: 'info', icon: AssessmentIcon },
];

function Reports() {
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const { data: productsData } = useGetProductsQuery({ limit: 1000 });
  const { data: salesData } = useGetSalesOrdersQuery({ limit: 1000 });
  const { data: salesReturnsData } = useGetSalesReturnsQuery({ limit: 1000 });
  const { data: purchaseData } = useGetPurchaseOrdersQuery({ limit: 1000 });
  const { data: purchaseReturnsData } = useGetPurchaseReturnsQuery({ limit: 1000 });

  const totalProducts = productsData?.products?.length || 0;
  
  const totalSales =
    salesData?.salesOrders?.reduce((sum, order) => sum + parseFloat(order.totalAmount || 0), 0) || 0;
  const totalSalesReturns = 
    salesReturnsData?.salesReturns?.reduce((sum, ret) => sum + parseFloat(ret.totalAmount || 0), 0) || 0;
  const netSales = totalSales - totalSalesReturns;

  const totalPurchases =
    purchaseData?.purchaseOrders?.reduce(
      (sum, order) => sum + parseFloat(order.totalAmount || 0),
      0
    ) || 0;
  const totalPurchaseReturns =
    purchaseReturnsData?.purchaseReturns?.reduce((sum, ret) => sum + parseFloat(ret.totalAmount || 0), 0) || 0;
  const netPurchases = totalPurchases - totalPurchaseReturns;

  const grossProfit = netSales - netPurchases;

  const stats = {
    products: totalProducts,
    sales: netSales,
    purchases: netPurchases,
    profit: grossProfit,
  };

  const formatAmount = (v) => {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(Number(v) || 0);
    } catch {
      return `${currency} ${(Number(v) || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatStat = (key, value) => {
    if (key === 'products') return value.toLocaleString();
    return formatAmount(value);
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          Reports & Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
          View and export business reports by category
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {STAT_CARDS.map(({ key, label, color, icon: Icon }) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={key}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: alpha(theme.palette[color]?.main || theme.palette.divider, 0.2),
                bgcolor: 'background.paper',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: alpha(theme.palette[color]?.main || theme.palette.divider, 0.4),
                  boxShadow: `0 8px 24px ${alpha(theme.palette[color]?.main || theme.palette.common.black, 0.08)}`,
                },
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: `${color}.main`,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {formatStat(key, stats[key])}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.1),
                      color: `${color}.main`,
                    }}
                  >
                    <Icon sx={{ fontSize: 24 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, mb: 2, color: 'text.primary', letterSpacing: '-0.01em' }}
      >
        Report categories
      </Typography>

      <Grid container spacing={2}>
        {REPORT_CATEGORIES.map((category) => {
          const IconComponent = CATEGORY_ICONS[category.id] || AssessmentIcon;
          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={category.id}>
              <Card
                elevation={0}
                onClick={() => navigate(`/reports/category/${category.id}`)}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      flexShrink: 0,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                    }}
                  >
                    <IconComponent sx={{ fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {category.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {category.reports.length} reports
                    </Typography>
                  </Box>
                  <ChevronRightRoundedIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default Reports;
